// @ts-nocheck

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, X, Image } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface CoverPhotoUploadProps {
  currentImageUrl?: string | null;
  onImageChange: (imageUrl: string | null) => void;
}

const CoverPhotoUpload: React.FC<CoverPhotoUploadProps> = ({
  currentImageUrl,
  onImageChange
}) => {
  const [uploading, setUploading] = useState(false);

  const getPreviewUrl = (filename: string | null | undefined): string => {
    if (!filename) return '';
    if (filename.startsWith('http')) return filename;
    return `https://pvaumvowzncvrdkkvnqq.supabase.co/storage/v1/object/public/event-featured/${filename}`;
  };

  const previewUrl = getPreviewUrl(currentImageUrl);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);

      const file = event.target.files?.[0];
      if (!file) return;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('You must be logged in to upload images');
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `cover-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('event-featured')
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type
        });

      if (uploadError) {
        if (uploadError.message?.includes('row-level security')) {
          toast.error('Permission denied. Please ensure you are logged in as an admin.');
        } else if (uploadError.message?.includes('Payload too large')) {
          toast.error('File is too large. Maximum size is 5MB.');
        } else {
          toast.error(`Failed to upload image: ${uploadError.message || 'Unknown error'}`);
        }
        return;
      }

      if (!uploadData) {
        toast.error('Upload failed: No data returned');
        return;
      }

      onImageChange(fileName);
      toast.success('Cover photo uploaded successfully!');

    } catch (error) {
      console.error('Error uploading cover photo:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    onImageChange(null);
    toast.success('Cover photo removed');
  };

  return (
    <div className="space-y-4">
      <Label htmlFor="cover-photo">Cover Photo (Optional)</Label>

      {previewUrl ? (
        <div className="relative">
          <img
            src={previewUrl}
            alt="Cover photo preview"
            className="w-full max-w-md h-48 object-cover rounded-lg border"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
            onClick={handleRemoveImage}
          >
            <X className="h-4 w-4" />
          </Button>

          <div className="mt-2">
            <div className="relative">
              <Input
                id="cover-photo-replace"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <Button type="button" variant="outline" disabled={uploading} className="w-full">
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Uploading...' : 'Replace Image'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <Image className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-sm text-gray-500 mb-4">
            Upload a wide hero banner for your event page (optional)
          </p>
          <div className="relative">
            <Input
              id="cover-photo"
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              disabled={uploading}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <Button type="button" disabled={uploading}>
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Uploading...' : 'Choose Image'}
            </Button>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Wide hero banner shown at the top of your public event page. Recommended: 1600×600. If left blank, we'll use the default cover photo for your sport. Max file size: 5MB.
      </p>
    </div>
  );
};

export default CoverPhotoUpload;
